import type {
  WxrlAction,
  WxrlCondition,
  WxrlRuleTrigger,
} from '../services/wxrl-rules.service';
import { isSnFlagSet } from './wxrl-sn-flag';

function sqlLeft(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n);
}

function sqlLen(s: string): number {
  return s.length;
}

/**
 * Bloque de etiqueta de paso (20 caracteres fijos): tras trim, relleno a la derecha
 * con espacios hasta 20; si supera 20, 17 caracteres + '...' (20 visibles).
 */
function formatActionLabelColumn(labelSrc: string): string {
  const s = String(labelSrc ?? '').trim();
  if (s.length > 20) {
    return s.slice(0, 17) + '...';
  }
  return s.padEnd(20, ' ');
}

function isConditionDetailEmpty(c: WxrlCondition): boolean {
  const neg = isSnFlagSet(c.negCond);
  const has =
    String(c.condId ?? '').trim() ||
    String(c.saveValue ?? '').trim() ||
    String(c.catalogROperator ?? '').trim() ||
    String(c.labelOperator ?? '').trim() ||
    String(c.rOperator ?? '').trim() ||
    String(c.entryValue ?? '').trim() ||
    String(c.kommentar ?? '').trim();
  return !has && !neg;
}

/**
 * Paridad con wspi_XRules_TreeTable (condiciones): anchuras y literales como el SQL.
 */
export function spFormatConditionDetail(
  condition: WxrlCondition,
  negadoWord: string,
  sinInfo: string
): string {
  if (isConditionDetailEmpty(condition)) {
    return sinInfo;
  }

  const condId = String(condition.condId ?? '');
  const saveValue = String(condition.saveValue ?? '');
  const cat = condition.catalogROperator;
  const catOk = cat != null && String(cat).trim() !== '';
  const opSource = catOk
    ? String(cat).trim()
    : (condition.labelOperator && String(condition.labelOperator).trim()) !== ''
      ? String(condition.labelOperator)
      : String(condition.rOperator ?? '');
  const entryValue = String(condition.entryValue ?? '');
  const kommentar = String(condition.kommentar ?? '');

  let detail = '';
  detail +=
    sqlLeft(condId, 12) + ' '.repeat(Math.max(0, 14 - sqlLen(condId)));
  detail +=
    sqlLeft(saveValue, 10) + ' '.repeat(Math.max(0, 12 - sqlLen(saveValue)));
  detail += sqlLeft(opSource, 10) + ' '.repeat(Math.max(0, 12 - sqlLen(opSource)));

  const evLeft = sqlLeft(entryValue, 20);
  const evTrimLen = entryValue.trim().length;
  const dots = evTrimLen > 20 ? '...' : '';
  detail += evLeft + dots + ' '.repeat(Math.max(0, 25 - sqlLen(entryValue)));

  if (isSnFlagSet(condition.negCond)) {
    detail += negadoWord + ' '.repeat(4);
  } else {
    detail += ' '.repeat(10);
  }

  detail += `(${kommentar})`;
  return detail;
}

function padNumericField(val: string | number | null | undefined, width: number): string {
  const t = String(val ?? '').trim();
  return t.padStart(width, ' ');
}

/** ActionValue / Additional: hasta `width` chars (left del valor) y pad a la derecha + espaciado final. */
function actionTextField(
  val: string | null | undefined,
  width: number,
  trailingSpaces: number
): string {
  const t = sqlLeft(String(val ?? '').trimEnd(), width);
  return t.padEnd(width, ' ') + ' '.repeat(trailingSpaces);
}

/**
 * Paridad con sección Pasos del SP (RULESACTION + LabelAction).
 */
export function spFormatActionStepDetail(
  action: WxrlAction,
  labelActionFromCatalog: string,
  inheritedLabel: string
): string {
  const rawMeth = action.methNr;
  const methNum = Number(rawMeth);
  const methStr = rawMeth === null || rawMeth === undefined ? '' : String(rawMeth).trim();
  const isInherited = methStr === '-1' || methNum === -1;

  const labelSrc =
    (labelActionFromCatalog && labelActionFromCatalog.trim()) || action.actionKrz || '';
  const labelPart = formatActionLabelColumn(labelSrc) + ' '.repeat(2);

  let mid: string;
  if (isInherited) {
    mid = inheritedLabel + ' '.repeat(2);
  } else {
    mid = padNumericField(methStr || String(rawMeth ?? ''), 10) + ' '.repeat(4);
  }

  const grup =
    padNumericField(action.grupNr, 10) + ' '.repeat(2);
  const textNr =
    padNumericField(action.textNr, 10) + ' '.repeat(2);
  const av = actionTextField(action.actionValue, 15, 3);
  const a1 = actionTextField(action.additional1, 15, 5);
  const a2 = actionTextField(action.additional2, 15, 5);
  // Tree-table UI (#769): paréntesis finales = Kommentar del paso, no RuleActionDesc.
  const comment = String(action.kommentar ?? '').trimEnd();
  return labelPart + mid + grup + textNr + av + a1 + a2 + ` (${comment})`;
}

/** Cabecera acciones SP: '(' + RuleActionNr + ')' + RuleActionDesc */
export function spFormatActionHeaderDetail(
  ruleActionNr: number | null | undefined,
  ruleActionDesc: string | undefined
): string {
  const nr = ruleActionNr != null && !Number.isNaN(Number(ruleActionNr))
    ? String(ruleActionNr)
    : '';
  const desc = String(ruleActionDesc ?? '').trimEnd();
  return `(${nr})${desc}`;
}

function triggerPrefix(subjectType: string): string {
  const st = String(subjectType ?? '').trim();
  if (st === 'RXPR' || st === 'Prueba') return 'Metodo - ';
  if (st === 'RXTX' || st === 'Texto') return 'Texto - ';
  if (st === 'RXAL' || st === 'Alarma') return 'Alarma - ';
  return `${st} - `;
}

/** CodElemento disparadores en SP: Metodo|Texto|Alarma + PosR */
export function spFormatTriggerElemento(trigger: WxrlRuleTrigger): string {
  return `${triggerPrefix(trigger.subjectType)}${String(trigger.posR ?? '')}`;
}

function triggerSubjectCode(trigger: WxrlRuleTrigger): string {
  const raw = trigger.subjectID as unknown;
  if (raw == null || raw === '') {
    return '';
  }
  return String(raw).trim();
}

function triggerNameAlreadyIncludesCode(name: string, id: string): boolean {
  if (!id) {
    return false;
  }
  return (
    name === id ||
    name.startsWith(`${id} `) ||
    name.startsWith(`${id}(`) ||
    name.startsWith(`(${id})`)
  );
}

/**
 * Columna «Elemento» del TreeTable (UI, #773): código del disparador + nombre.
 * En edición el drawer deja a veces el label de catálogo (`211110 (Text) Rh`);
 * al recargar, `subjectName` es solo el nombre y hay que anteponer `subjectID`.
 * Si faltan ambos, se usa el CodElemento estilo SP (#537).
 */
export function formatTriggerElementoUiLabel(trigger: WxrlRuleTrigger): string {
  const name = String(trigger.subjectName ?? '').trim();
  const id = triggerSubjectCode(trigger);
  if (name && id) {
    return triggerNameAlreadyIncludesCode(name, id) ? name : `${id} ${name}`;
  }
  if (name) {
    return name;
  }
  if (id) {
    return id;
  }
  return spFormatTriggerElemento(trigger);
}

/**
 * Columna «Descripción / Detalle» de disparadores (#770): evento disparador
 * (etiqueta SCHLUESSEL, o código StorProType) y comentario si existe.
 * El SP histórico solo ponía Kommentar; el tree-table de la UI debe mostrar el evento.
 */
export function spFormatTriggerDetail(
  trigger: WxrlRuleTrigger,
  sinInfo: string
): string {
  const eventLabel =
    String(trigger.triggEventDescription ?? '').trim() ||
    String(trigger.triggEvent ?? '').trim();
  const k = String(trigger.kommentar ?? '').trim();
  if (eventLabel && k) {
    return `${eventLabel} (${k})`;
  }
  return eventLabel || k || sinInfo;
}
